import type { CrawlerEvidenceRowResponse, ProviderEvidenceResponse } from '@prerender/diagnostics-api';
import { useId } from 'react';
import { Table } from 'react-bootstrap';

import {
  affectedDomainsLabel,
  formatDropAsChange,
  formatRequestCount,
  spansSeveralDomains,
  worstDomainLabel,
} from './crawlerEvidenceFigures';
import { crawlerLabel, providerCrawlerLabel } from './crawlerLabels';
import CrawlerStandingCell from './CrawlerStandingCell';
import { EVIDENCE_TITLE } from './EvidenceTable';
import { formatInstantOrAbsent } from './formatDetectedAt';

type CrawlerEvidenceProps = {
  crawlerEvidence: readonly ProviderEvidenceResponse[];
};


const FIGURE_COLUMNS = [
  { label: 'Domains', numeric: false },
  { label: 'Last 7 days', numeric: true },
  { label: 'Usual week', numeric: true },
  { label: 'Change', numeric: true },
  { label: 'Last request', numeric: false },
  { label: 'Severity', numeric: false },
];

export const NUMERIC_CELL = 'diagnostics-cell-numeric';
const LAST_MEASURED = 'last measured';

export function LastMeasuredNote() {
  return <div className="diagnostics-evidence-note">{LAST_MEASURED}</div>;
}

type CrawlerRowProps = {
  label: string;
  row: CrawlerEvidenceRowResponse;
  nested: boolean;
};

function CrawlerRow({ label, row, nested }: CrawlerRowProps) {
  return (
    <tr className={row.noLongerAffected ? 'diagnostics-evidence-recovered' : undefined}>
      <th scope="row" className={nested ? 'diagnostics-evidence-nested-crawler' : undefined}>
        {label}
      </th>
      <td>{affectedDomainsLabel(row.noLongerAffected ? row.totalledDomainCount : row.affectedDomainCount)}</td>
      <td className={NUMERIC_CELL}>{formatRequestCount(row.currentRequests)}</td>
      <td className={NUMERIC_CELL}>{formatRequestCount(row.usualWeekRequests)}</td>
      <td className={NUMERIC_CELL}>
        <div>{formatDropAsChange(row.metricValue)}</div>
        {spansSeveralDomains(row) && (
          <div className="diagnostics-evidence-note">{worstDomainLabel(row.worstMetricValue, row.worstDomain)}</div>
        )}
        {row.noLongerAffected && <LastMeasuredNote />}
      </td>
      <td>{formatInstantOrAbsent(row.lastRequestAt)}</td>
      <CrawlerStandingCell severity={row.severity} noLongerAffected={row.noLongerAffected} />
    </tr>
  );
}

function ProviderRows({
  provider,
  severity,
  metricValue,
  currentRequests,
  usualWeekRequests,
  lastRequestAt,
  crawlers,
  noLongerAffected,
}: ProviderEvidenceResponse) {
  if (crawlers.length === 1) {
    const [onlyCrawler] = crawlers;
    return <CrawlerRow label={providerCrawlerLabel(provider, onlyCrawler.crawler)} row={onlyCrawler} nested={false} />;
  }
  return (
    <>
      <tr className={noLongerAffected ? 'diagnostics-evidence-recovered' : undefined}>
        <th scope="rowgroup">{provider}</th>
        <td />
        <td className={NUMERIC_CELL}>{formatRequestCount(currentRequests)}</td>
        <td className={NUMERIC_CELL}>{formatRequestCount(usualWeekRequests)}</td>
        <td className={NUMERIC_CELL}>
          <div>{formatDropAsChange(metricValue)}</div>
          {noLongerAffected && <LastMeasuredNote />}
        </td>
        <td>{formatInstantOrAbsent(lastRequestAt)}</td>
        <CrawlerStandingCell severity={severity} noLongerAffected={noLongerAffected} />
      </tr>
      {crawlers.map((row) => (
        <CrawlerRow key={row.crawler} label={crawlerLabel(row.crawler)} row={row} nested />
      ))}
    </>
  );
}

function CrawlerEvidence({ crawlerEvidence }: CrawlerEvidenceProps) {
  const titleId = useId();

  return (
    <section className="diagnostics-detail-section" aria-labelledby={titleId}>
      <h2 id={titleId} className="diagnostics-detail-section-title">
        {EVIDENCE_TITLE}
      </h2>
      <Table className="diagnostics-evidence-table diagnostics-crawler-evidence-table" aria-label={EVIDENCE_TITLE}>
        <thead>
          <tr>
            <th scope="col">Crawler</th>
            {FIGURE_COLUMNS.map(({ label, numeric }) => (
              <th key={label} scope="col" className={numeric ? NUMERIC_CELL : undefined}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        {crawlerEvidence.map((providerEvidence) => (
          <tbody key={providerEvidence.provider}>
            <ProviderRows {...providerEvidence} />
          </tbody>
        ))}
      </Table>
    </section>
  );
}

export default CrawlerEvidence;
